import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import pb from '../lib/pocketbase';
import { Calendar, Clock, ChevronRight, BookOpen, User, ArrowLeft, Send, CheckCircle, Info, Facebook, Twitter, MessageCircle } from 'lucide-react';

const Blog = () => {
  // ... rest of component logic (no changes)
  const navigate = useNavigate();
  const { slug } = useParams();
  const [articles, setArticles] = useState([]);
  const [currentArticle, setCurrentArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("Tous");
  const [categories, setCategories] = useState(["Tous"]);

  useEffect(() => {
    loadArticles();
  }, []);

  useEffect(() => {
    if (slug) {
      loadSingleArticle(slug);
    } else {
      setCurrentArticle(null);
    }
  }, [slug]);

  const loadArticles = async () => {
    try {
      setLoading(true);
      const records = await pb.collection('articles').getFullList({
        filter: 'status = "published"',
        sort: '-created',
        expand: 'author'
      });
      setArticles(records);
      const uniqueCategories = ["Tous", ...new Set(records.map(article => article.category).filter(Boolean))];
      setCategories(uniqueCategories);
    } catch (error) {
      console.error('Erreur PocketBase:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSingleArticle = async (articleSlug) => {
    try {
      setLoading(true);
      const record = await pb.collection('articles').getFirstListItem(`slug="${articleSlug}"`, {
        expand: 'author'
      });
      setCurrentArticle(record);
      window.scrollTo(0, 0);
    } catch (error) {
      console.error('Article non trouvé:', error);
      navigate('/blog');
    } finally {
      setLoading(false);
    }
  };

  const filteredArticles = selectedCategory === "Tous" 
    ? articles 
    : articles.filter(a => a.category === selectedCategory);

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('fr-FR', options);
  };

  const calculateReadTime = (content) => {
    const wordsPerMinute = 200;
    const wordCount = (content || "").split(' ').length;
    return `${Math.ceil(wordCount / wordsPerMinute)} min`;
  };

  if (loading) return (
    <div className="loading-state">
      <div className="spinner"></div>
      <style jsx>{`
        .loading-state { height: 100vh; display: flex; align-items: center; justify-content: center; background: #f8fafc; }
        .spinner { width: 50px; height: 50px; border: 5px solid #e2e8f0; border-top-color: #3b82f6; border-radius: 50%; animation: spin 1s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );

  // Vue Détail (Proposition 2: Le Journal de l'Expert)
  if (currentArticle) {
    return (
      <div className="blog-detail-page">
        {/* Dynamic Hero */}
        <section className="article-hero" style={{ 
          backgroundImage: `url(https://christophedepanne.infocolab.online/api/files/articles/${currentArticle.id}/${currentArticle.image?.[0]})` 
        }}>
          <div className="hero-overlay"></div>
          <div className="container hero-content">
            <Link to="/blog" className="back-link">
              <ArrowLeft size={18} /> Retour au Blog
            </Link>
            <span className="hero-badge">{currentArticle.category}</span>
            <h1 className="hero-title">{currentArticle.title}</h1>
            <div className="hero-meta">
              <div className="meta-item"><Calendar size={18} /> {formatDate(currentArticle.created)}</div>
              <div className="meta-item"><Clock size={18} /> {calculateReadTime(currentArticle.content)} de lecture</div>
              <div className="meta-item"><User size={18} /> Christophe Dépanne</div>
            </div>
          </div>
        </section>

        <section className="article-main">
          <div className="container">
            <div className="article-layout">
              {/* Colonne Gauche: Contenu Article */}
              <div className="article-content-col">
                <div className="article-body-text" dangerouslySetInnerHTML={{ __html: currentArticle.content }}></div>
                
                {/* Bloc Verdict de l'Expert (Design System Light Blue) */}
                <div className="expert-verdict-box shadow-lg">
                  <div className="verdict-header">
                    <CheckCircle size={32} color="#3b82f6" />
                    <h3>Le Verdict de Christophe</h3>
                  </div>
                  <p>
                    {currentArticle.excerpt || "Pour ce type d'entretien, je recommande toujours de vérifier l'état général de vos pneumatiques et de vos niveaux avant de reprendre la route."}
                  </p>
                </div>

                {/* Share bar */}
                {/* Share bar (Premium Style) */}
                <div className="article-footer-section">
                  <div className="share-bar shadow-sm">
                    <span className="share-label">Partager l'expertise :</span>
                    <div className="share-icons">
                      <button className="share-btn fb" aria-label="Partager sur Facebook"><Facebook size={20} /></button>
                      <button className="share-btn tw" aria-label="Partager sur X (Twitter)"><Twitter size={20} /></button>
                      <button className="share-btn wa" aria-label="Partager sur WhatsApp"><MessageCircle size={20} /></button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Colonne Droite: Sidebar Expert */}
              <aside className="article-sidebar">
                {/* Carte Expert */}
                <div className="sidebar-card expert-card shadow-xl">
                  <div className="expert-avatar-box">
                    <img src="/images/logo.jpeg" alt="Christophe" />
                    <div className="status-dot"></div>
                  </div>
                  <h3>Besoin d'un conseil ?</h3>
                  <p>J'interviens 24h/24 pour tout diagnotic ou réparation à Lahage et ses environs.</p>
                  <button onClick={() => navigate('/contact')} className="sidebar-cta-btn">Parler à Christophe</button>
                </div>

                {/* Articles Similaires */}
                <div className="sidebar-section">
                  <h4 className="section-title">Articles Similaires</h4>
                  <div className="related-list">
                    {articles.filter(a => a.id !== currentArticle.id).slice(0, 3).map(a => (
                      <Link key={a.id} to={`/blog/${a.slug}`} className="related-item">
                        <div className="related-thumb">
                          <img src={`https://christophedepanne.infocolab.online/api/files/articles/${a.id}/${a.image?.[0]}`} alt={a.title} />
                        </div>
                        <div className="related-info">
                          <h6>{a.title}</h6>
                          <span>{formatDate(a.created)}</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Newsletter */}
                <div className="sidebar-card newsletter-card">
                  <Info size={32} color="#3b82f6" />
                  <h4>Restez informé</h4>
                  <p>Recevez nos derniers conseils mécaniques par email.</p>
                  <div className="newsletter-form">
                    <input type="email" placeholder="Votre email..." />
                    <button className="newsletter-btn"><Send size={18} /></button>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>

        <style jsx>{`
          .blog-detail-page { background: #ffffff; }
          .container { max-width: 1300px; margin: 0 auto; padding: 0 30px; }

          /* Hero Section */
          .article-hero {
            position: relative;
            height: 65vh;
            min-height: 550px;
            background-size: cover;
            background-position: center;
            display: flex;
            align-items: center;
            color: white;
            padding-top: 80px;
          }

          .hero-overlay {
            position: absolute;
            inset: 0;
            background: linear-gradient(to top, rgba(15, 23, 42, 0.95), rgba(15, 23, 42, 0.4));
          }

          .hero-content { position: relative; z-index: 2; width: 100%; }

          .back-link {
            display: flex;
            align-items: center;
            gap: 10px;
            color: #94a3b8;
            text-decoration: none;
            font-weight: 700;
            margin-bottom: 30px;
            transition: 0.3s;
          }

          .back-link:hover { color: #ffffff; transform: translateX(-5px); }

          .hero-badge {
            background: #3b82f6;
            color: white;
            padding: 8px 24px;
            border-radius: 50px;
            font-weight: 900;
            text-transform: uppercase;
            font-size: 0.9rem;
            letter-spacing: 2px;
            display: inline-block;
            margin-bottom: 30px;
          }

          .hero-title {
            font-size: 4.5rem;
            font-weight: 900;
            line-height: 1.1;
            margin-bottom: 40px;
            max-width: 1000px;
          }

          .hero-meta { display: flex; gap: 40px; flex-wrap: wrap; }
          .meta-item { display: flex; align-items: center; gap: 12px; font-weight: 600; color: #cbd5e1; font-size: 1.1rem; }

          /* Layout Articles */
          .article-main { padding: 80px 0; }
          .article-layout { display: grid; grid-template-columns: 1fr 350px; gap: 80px; }

          .article-body-text {
            font-size: 1.3rem;
            line-height: 1.8;
            color: #1e293b;
          }

          .article-body-text :global(h2) { font-size: 2.2rem; font-weight: 900; color: #0f172a; margin: 60px 0 30px; }
          .article-body-text :global(p) { margin-bottom: 30px; }
          .article-body-text :global(img) { width: 100%; border-radius: 24px; margin: 40px 0; }

          /* Verdict Box */
          .expert-verdict-box {
            background: #f0f9ff;
            border-left: 8px solid #3b82f6;
            padding: 40px;
            border-radius: 24px;
            margin: 60px 0;
          }

          .verdict-header { display: flex; align-items: center; gap: 15px; margin-bottom: 20px; }
          .verdict-header h3 { font-size: 1.8rem; font-weight: 900; color: #0f172a; }
          .expert-verdict-box p { font-size: 1.25rem; color: #1e293b; font-style: italic; line-height: 1.6; }

          /* Article Footer Section */
          .article-footer-section { padding: 40px 0; border-top: 2px solid #f1f5f9; margin-top: 60px; }
          .share-bar { display: flex; align-items: center; justify-content: space-between; background: #f8fafc; padding: 25px 40px; border-radius: 20px; }
          .share-label { color: #1e293b; font-weight: 800; font-size: 1.1rem; }
          .share-icons { display: flex; gap: 15px; }
          .share-btn { width: 50px; height: 50px; border-radius: 12px; border: none; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: 0.3s; background: white; color: #64748b; box-shadow: 0 4px 6px rgba(0,0,0,0.05); }
          
          .share-btn.fb:hover { background: #1877f2; color: white; transform: translateY(-3px); box-shadow: 0 10px 20px rgba(24, 119, 242, 0.3); }
          .share-btn.tw:hover { background: #000000; color: white; transform: translateY(-3px); box-shadow: 0 10px 20px rgba(0, 0, 0, 0.3); }
          .share-btn.wa:hover { background: #25d366; color: white; transform: translateY(-3px); box-shadow: 0 10px 20px rgba(37, 211, 102, 0.3); }

          /* Sidebar Styles */
          .article-sidebar { display: flex; flex-direction: column; gap: 40px; }

          .sidebar-card {
            background: #ffffff;
            border-radius: 30px;
            padding: 40px;
            text-align: center;
            border: 1px solid #f1f5f9;
          }

          .expert-avatar-box {
            width: 120px;
            height: 120px;
            margin: 0 auto 30px;
            position: relative;
          }

          .expert-avatar-box img {
            width: 100%; height: 100%; border-radius: 50%; object-fit: cover; border: 4px solid #3b82f6; padding: 5px;
          }

          .status-dot {
            position: absolute; bottom: 8px; right: 8px; width: 24px; height: 24px; background: #10b981; border: 4px solid white; border-radius: 50%;
          }

          .sidebar-card h3 { font-size: 1.6rem; font-weight: 900; color: #0f172a; margin-bottom: 15px; }
          .sidebar-card p { color: #64748b; font-weight: 600; line-height: 1.5; margin-bottom: 30px; }

          .sidebar-cta-btn {
            width: 100%; padding: 18px; background: #3b82f6; color: white; border: none; border-radius: 14px; font-weight: 800; text-transform: uppercase; cursor: pointer; transition: 0.3s;
          }
          .sidebar-cta-btn:hover { background: #2563eb; transform: translateY(-3px); }

          .sidebar-section { padding: 0 10px; }
          .section-title { font-size: 1.3rem; font-weight: 900; color: #0f172a; margin-bottom: 25px; }

          .related-list { display: flex; flex-direction: column; gap: 25px; }
          .related-item { display: flex; gap: 15px; text-decoration: none; transition: 0.3s; }
          .related-item:hover { transform: translateX(5px); }

          .related-thumb { width: 80px; height: 80px; border-radius: 12px; overflow: hidden; flex-shrink: 0; }
          .related-thumb img { width: 100%; height: 100%; object-fit: cover; }
          .related-info h6 { font-size: 1rem; color: #1e293b; font-weight: 800; margin-bottom: 5px; line-height: 1.3; }
          .related-info span { font-size: 0.85rem; color: #94a3b8; font-weight: 600; }

          .newsletter-card { background: #f8fafc; }
          .newsletter-form { display: flex; gap: 10px; margin-top: 20px; }
          .newsletter-form input { flex: 1; padding: 15px; border-radius: 12px; border: 1px solid #e2e8f0; outline: none; }
          .newsletter-btn { background: #3b82f6; color: white; border: none; padding: 0 20px; border-radius: 12px; cursor: pointer; }

          @media (max-width: 1200px) {
            .article-layout { grid-template-columns: 1fr; }
            .article-sidebar { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; }
            .hero-title { font-size: 3rem; }
          }
          @media (max-width: 768px) {
            .article-sidebar { grid-template-columns: 1fr; }
            .hero-title { font-size: 2.5rem; }
            .hero-meta { gap: 20px; }
          }
        `}</style>
      </div>
    );
  }

  // Vue Liste (Même style premium que précédemment)
  return (
    <div className="blog-page">
      {/* Hero Section */}
      <section className="blog-hero">
        <div className="hero-overlay"></div>
        <div className="container hero-content">
          <span className="hero-badge">Actualités de l'Atelier</span>
          <h1 className="hero-title">Le Blog <span>Christophe Dépanne</span></h1>
          <p className="hero-subtitle">
            Découvrez nos derniers conseils, astuces mécaniques et actualités pour entretenir 
            votre véhicule comme un professionnel.
          </p>
        </div>
      </section>

      {/* Catégories */}
      <section className="category-section">
        <div className="container">
          <div className="filter-bar">
            {categories.map(c => (
              <button 
                key={c} 
                className={`filter-btn ${selectedCategory === c ? 'active' : ''}`}
                onClick={() => setSelectedCategory(c)}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Grille Articles */}
      <section className="articles-section">
        <div className="container">
          <div className="blog-grid">
            {filteredArticles.map(article => (
              <article key={article.id} className="blog-card" onClick={() => navigate(`/blog/${article.slug}`)}>
                <div className="card-image-box">
                  <img src={`https://christophedepanne.infocolab.online/api/files/articles/${article.id}/${article.image?.[0]}`} alt={article.title} />
                  <span className="card-badge">{article.category}</span>
                </div>
                <div className="card-body">
                  <div className="card-meta">
                    <span className="meta-item"><Calendar size={14} /> {formatDate(article.created)}</span>
                    <span className="meta-item"><Clock size={14} /> {calculateReadTime(article.content)}</span>
                  </div>
                  <h2 className="card-title">{article.title}</h2>
                  <p className="card-excerpt">{article.excerpt || "Consultez l'article complet pour découvrir nos conseils d'experts..."}</p>
                  <div className="card-footer">
                    <span className="read-more">Lire l'article <ChevronRight size={18} /></span>
                  </div>
                </div>
              </article>
            ))}
          </div>

          {filteredArticles.length === 0 && (
            <div className="empty-catalog">
              <BookOpen size={64} color="#e2e8f0" />
              <h3>Aucun article n'est disponible pour le moment</h3>
              <p>Revenez nous voir bientôt pour de nouveaux conseils mécaniques !</p>
            </div>
          )}
        </div>
      </section>

      {/* Section CTA */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-card">
            <div className="cta-content">
              <h2>Besoin d'un conseil personnalisé ?</h2>
              <p>Nos experts sont là pour répondre à toutes vos questions techniques et entretenir votre véhicule.</p>
            </div>
            <button onClick={() => navigate('/contact')} className="cta-btn">Prendre Rendez-vous</button>
          </div>
        </div>
      </section>

      <style jsx>{`
        .blog-page { background-color: #f8fafc; padding-bottom: 100px; }
        .container { max-width: 1200px; margin: 0 auto; padding: 0 20px; }
        .blog-hero { position: relative; height: 65vh; min-height: 500px; background: url('/images/slider/slide4.png') center/cover no-repeat; display: flex; align-items: center; justify-content: center; text-align: center; color: white; overflow: hidden; }
        .hero-overlay { position: absolute; inset: 0; background: linear-gradient(to top, rgba(26, 36, 47, 0.9), rgba(26, 36, 47, 0.5)); z-index: 1; }
        .hero-content { position: relative; z-index: 2; max-width: 850px; }
        .hero-badge { background: #3b82f6; color: white; padding: 6px 16px; border-radius: 50px; font-weight: 700; font-size: 0.85rem; text-transform: uppercase; letter-spacing: 2px; margin-bottom: 25px; display: inline-block; }
        .hero-title { font-size: 3.5rem; font-weight: 900; margin-bottom: 25px; line-height: 1.1; }
        .hero-title span { color: #3b82f6; }
        .hero-subtitle { font-size: 1.25rem; opacity: 0.8; max-width: 650px; margin: 0 auto; line-height: 1.6; }
        .category-section { margin-top: 40px; margin-bottom: 60px; }
        .filter-bar { display: flex; justify-content: center; gap: 15px; flex-wrap: wrap; }
        .filter-btn { padding: 10px 25px; border-radius: 50px; border: 1px solid #e2e8f0; background: white; color: #64748b; font-weight: 600; cursor: pointer; transition: all 0.3s; }
        .filter-btn.active, .filter-btn:hover { background: #1e293b; color: white; border-color: #1e293b; }
        .blog-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)); gap: 30px; }
        .blog-card { background: white; border-radius: 20px; overflow: hidden; box-shadow: 0 10px 30px rgba(0,0,0,0.05); transition: all 0.3s ease; border: 1px solid rgba(0,0,0,0.05); cursor: pointer; }
        .blog-card:hover { transform: translateY(-10px); box-shadow: 0 20px 40px rgba(0,0,0,0.1); }
        .card-image-box { position: relative; height: 220px; overflow: hidden; }
        .card-image-box img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.5s; }
        .blog-card:hover .card-image-box img { transform: scale(1.1); }
        .card-badge { position: absolute; top: 15px; left: 15px; background: #3b82f6; color: white; padding: 5px 15px; border-radius: 8px; font-size: 0.8rem; font-weight: 700; text-transform: uppercase; }
        .card-body { padding: 30px; }
        .card-meta { display: flex; gap: 20px; color: #94a3b8; font-size: 0.85rem; margin-bottom: 15px; }
        .meta-item { display: flex; align-items: center; gap: 8px; }
        .card-title { font-size: 1.4rem; color: #1e293b; font-weight: 800; margin-bottom: 15px; line-height: 1.3; }
        .card-excerpt { color: #64748b; line-height: 1.6; font-size: 1rem; margin-bottom: 25px; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
        .card-footer { padding-top: 20px; border-top: 1px solid #f1f5f9; }
        .read-more { color: #3b82f6; font-weight: 800; display: flex; align-items: center; gap: 5px; font-size: 0.95rem; }
        .cta-section { padding-top: 100px; }
        .cta-card { background: #1e293b; border-radius: 24px; padding: 60px; display: flex; justify-content: space-between; align-items: center; color: white; gap: 40px; background-image: radial-gradient(circle at top right, rgba(59, 130, 246, 0.2), transparent); }
        .cta-content h2 { font-size: 2.5rem; font-weight: 900; margin-bottom: 15px; }
        .cta-content p { color: #94a3b8; font-size: 1.2rem; }
        .cta-btn { background: #3b82f6; color: white; padding: 18px 40px; border-radius: 12px; border: none; font-weight: 800; cursor: pointer; white-space: nowrap; transition: all 0.3s; text-transform: uppercase; }
        .cta-btn:hover { background: #2563eb; transform: scale(1.05); box-shadow: 0 10px 20px rgba(37, 99, 235, 0.3); }
        .empty-catalog { text-align: center; padding: 100px 0; color: #94a3b8; }
        .empty-catalog h3 { color: #1e293b; margin: 20px 0 10px; }
        @media (max-width: 992px) { .cta-card { flex-direction: column; text-align: center; padding: 40px; } .hero-title { font-size: 2.8rem; } }
        @media (max-width: 480px) { .card-body { padding: 20px; } .cta-btn { width: 100%; } }
      `}</style>
    </div>
  );
};

export default Blog;
